// Central API helpers for All Saints Catholic Parish frontend
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'asc_admin_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    // If token invalid, clear it (component-level redirect handled by AuthContext)
    if (err?.response?.status === 401 && getToken()) {
      const url = err?.config?.url || '';
      // Don't clear on login attempts
      if (!url.includes('/auth/login')) {
        clearToken();
        // Notify listeners
        window.dispatchEvent(new Event('asc-auth-expired'));
      }
    }
    return Promise.reject(err);
  }
);

export default client;

// Convenience wrappers per resource
export const api = {
  // Auth
  login: (username, password) => client.post('/auth/login', { username, password }),
  me: () => client.get('/auth/me'),

  // Bulletins
  listBulletins: () => client.get('/bulletins'),
  createBulletin: (body) => client.post('/bulletins', body),
  updateBulletin: (id, body) => client.put(`/bulletins/${id}`, body),
  deleteBulletin: (id) => client.delete(`/bulletins/${id}`),
  uploadBulletin: (formData) => client.post('/bulletins/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  bulletinDownloadUrl: (id) => `${API}/bulletins/${id}/download`,

  // News
  listNews: () => client.get('/news'),
  createNews: (body) => client.post('/news', body),
  updateNews: (id, body) => client.put(`/news/${id}`, body),
  deleteNews: (id) => client.delete(`/news/${id}`),
  reorderNews: (ids) => client.post('/news/reorder', { ids }),
  // Home-featured news: admin stars news items to show under "What's Happening" on home
  featureNewsOnHome: (id, featured) => client.post(`/news/${encodeURIComponent(id)}/feature-home`, { featured }),
  listFeaturedNews: () => client.get('/news/featured'),
  reorderFeaturedNews: (ids) => client.post('/news/featured/reorder', { ids }),

  // Ministries
  listMinistries: () => client.get('/ministries'),
  createMinistry: (body) => client.post('/ministries', body),
  updateMinistry: (id, body) => client.put(`/ministries/${id}`, body),
  deleteMinistry: (id) => client.delete(`/ministries/${id}`),
  reorderMinistries: (ids) => client.post('/ministries/reorder', { ids }),
  uploadMinistryImage: (id, formData) => client.post(
    `/ministries/${encodeURIComponent(id)}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  deleteMinistryImage: (id) => client.delete(`/ministries/${encodeURIComponent(id)}/image`),
  ministryImageAbs: (path) => (path ? `${BACKEND_URL}${path}` : ''),

  // Generic page tiles (image + text cards scoped by section_key)
  listPageTiles: (sectionKey) => client.get(`/page-tiles/sections/${encodeURIComponent(sectionKey)}`),
  createPageTile: (sectionKey, body) => client.post(`/page-tiles/sections/${encodeURIComponent(sectionKey)}`, body),
  reorderPageTiles: (sectionKey, ids) => client.post(`/page-tiles/sections/${encodeURIComponent(sectionKey)}/reorder`, { ids }),
  updatePageTile: (tileId, body) => client.put(`/page-tiles/${tileId}`, body),
  deletePageTile: (tileId) => client.delete(`/page-tiles/${tileId}`),
  uploadPageTileImage: (tileId, formData) => client.post(
    `/page-tiles/${encodeURIComponent(tileId)}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  deletePageTileImage: (tileId) => client.delete(`/page-tiles/${encodeURIComponent(tileId)}/image`),
  pageTileImageAbs: (path) => (path ? `${BACKEND_URL}${path}` : ''),

  // Home-featured page tiles: admin can star any page tile to feature it on the home page
  featurePageTileOnHome: (tileId, featured) => client.post(
    `/page-tiles/${encodeURIComponent(tileId)}/feature-home`,
    { featured },
  ),
  listHomeFeaturedTiles: () => client.get('/home-featured-tiles'),
  reorderHomeFeaturedTiles: (ids) => client.post('/home-featured-tiles/reorder', { ids }),
  // Unified home-featured reorder across BOTH news and page tiles.
  // items = [{ type: 'news' | 'tile', id: '...' }] in desired order.
  reorderHomeFeatured: (items) => client.post('/home-featured/reorder', { items }),

  // Hall rental requests (public POST, admin list/read/delete)
  createHallRental: (body) => client.post('/hall-rentals', body),
  listHallRentals: () => client.get('/hall-rentals'),
  markHallRentalRead: (id) => client.patch(`/hall-rentals/${id}/read`),
  deleteHallRental: (id) => client.delete(`/hall-rentals/${id}`),
  markRegistrationRead: (id) => client.patch(`/registrations/${id}/read`),
  inboxSummary: () => client.get('/admin/inbox-summary'),

  // Team
  listTeam: () => client.get('/team'),
  createTeam: (body) => client.post('/team', body),
  updateTeam: (id, body) => client.put(`/team/${id}`, body),
  deleteTeam: (id) => client.delete(`/team/${id}`),

  // Schedules
  listSchedules: () => client.get('/schedules'),
  createSchedule: (body) => client.post('/schedules', body),
  updateSchedule: (id, body) => client.put(`/schedules/${id}`, body),
  deleteSchedule: (id) => client.delete(`/schedules/${id}`),

  // Registrations
  createRegistration: (body) => client.post('/registrations', body),
  listRegistrations: () => client.get('/registrations'),
  deleteRegistration: (id) => client.delete(`/registrations/${id}`),

  // Contacts
  createContact: (body) => client.post('/contacts', body),
  listContacts: () => client.get('/contacts'),
  markContactRead: (id) => client.patch(`/contacts/${id}/read`),
  deleteContact: (id) => client.delete(`/contacts/${id}`),

  // Settings
  getSettings: () => client.get('/settings'),
  updateSettings: (body) => client.put('/settings', body),

  // Calendar events (public)
  listEvents: (limit = 3) => client.get(`/events?limit=${limit}`),

  // Prayer requests
  createPrayer: (body) => client.post('/prayers', body),
  listPrayers: () => client.get('/prayers'),
  markPrayerRead: (id) => client.patch(`/prayers/${id}/read`),
  deletePrayer: (id) => client.delete(`/prayers/${id}`),

  // Replies (admin outbox)
  emailStatus: () => client.get('/email/status'),
  sendReply: (body) => client.post('/replies', body),
  listReplies: () => client.get('/replies'),

  // Content blocks (editable text)
  listContent: () => client.get('/content'),
  getContent: (key) => client.get(`/content/${encodeURIComponent(key)}`),
  updateContent: (key, value) => client.put(`/content/${encodeURIComponent(key)}`, { value }),

  // Church images (Home page hero photos)
  getChurchImageMeta: (churchId) => client.get(`/church-image/${encodeURIComponent(churchId)}/meta`),
  uploadChurchImage: (churchId, formData) => client.post(
    `/church-image/${encodeURIComponent(churchId)}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  churchImageUrl: (churchId, cacheBust) => `${API}/church-image/${encodeURIComponent(churchId)}${cacheBust ? `?v=${cacheBust}` : ''}`,

  // Page images (Give sub-pages & other admin-managed page banners)
  getPageImageMeta: (slug) => client.get(`/page-image/${encodeURIComponent(slug)}/meta`),
  uploadPageImage: (slug, formData) => client.post(
    `/page-image/${encodeURIComponent(slug)}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  deletePageImage: (slug) => client.delete(`/page-image/${encodeURIComponent(slug)}`),
  pageImageUrl: (slug, cacheBust) => `${API}/page-image/${encodeURIComponent(slug)}${cacheBust ? `?v=${cacheBust}` : ''}`,

  // News item photos (admin-uploadable)
  uploadNewsImage: (newsId, formData) => client.post(
    `/news/${encodeURIComponent(newsId)}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  deleteNewsImage: (newsId) => client.delete(`/news/${encodeURIComponent(newsId)}/image`),
  // Absolutise the image_url returned by the API (path like /api/news/xxx/image?v=...)
  newsImageAbs: (path) => (path ? `${BACKEND_URL}${path}` : ''),

  // I'm New tiles (admin-managed)
  listImNewTiles: () => client.get('/im-new-tiles'),
  createImNewTile: (body) => client.post('/im-new-tiles', body),
  updateImNewTile: (id, body) => client.put(`/im-new-tiles/${id}`, body),
  deleteImNewTile: (id) => client.delete(`/im-new-tiles/${id}`),
  reorderImNewTiles: (ids) => client.post('/im-new-tiles/reorder', { ids }),
  uploadImNewTileImage: (id, formData) => client.post(
    `/im-new-tiles/${encodeURIComponent(id)}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  deleteImNewTileImage: (id) => client.delete(`/im-new-tiles/${encodeURIComponent(id)}/image`),
  imNewTileImageAbs: (path) => (path ? `${BACKEND_URL}${path}` : ''),

  // Helpful Links (admin-managed resource cards on News & Resources)
  listHelpfulLinks: () => client.get('/helpful-links'),
  createHelpfulLink: (body) => client.post('/helpful-links', body),
  updateHelpfulLink: (id, body) => client.put(`/helpful-links/${id}`, body),
  deleteHelpfulLink: (id) => client.delete(`/helpful-links/${id}`),
  reorderHelpfulLinks: (ids) => client.post('/helpful-links/reorder', { ids }),

  // Documents (admin-uploaded PDFs)
  getDocument: (key) => client.get(`/documents/${encodeURIComponent(key)}`),
  uploadDocument: (key, formData) => client.post(`/documents/${encodeURIComponent(key)}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteDocument: (key) => client.delete(`/documents/${encodeURIComponent(key)}`),
  documentDownloadUrl: (key) => `${API}/documents/${encodeURIComponent(key)}/download`,

  // Site visits (anonymous privacy-friendly counter — public POST, admin-only GET)
  trackVisit: () => client.post('/visits/track'),
  visitorStats: () => client.get('/admin/visits/stats'),

  // Admin: download a zip of every image on the site (JPG/PNG/WebP) for backup
  downloadMediaBackup: () => client.get('/admin/media/download-zip', { responseType: 'blob' }),

  // Google Drive storage status + one-shot migration of legacy disk uploads
  driveStatus: () => client.get('/admin/drive/status'),
  driveMigrate: () => client.post('/admin/drive/migrate'),

  // Bulletin subscribers (public POST, admin-only GET/DELETE)
  subscribeToBulletin: (email, website = '') => client.post('/bulletin-subscribers', { email, website }),
  listBulletinSubscribers: () => client.get('/bulletin-subscribers'),
  deleteBulletinSubscriber: (id) => client.delete(`/bulletin-subscribers/${id}`),
};
